import React from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

type DatePickerProps = {
    value: number | null; // Epoch in millisecond timestamp
    onChange: (date: number | null) => void; // Callback for when the date changes
    format?: string; // Date display format (default: MM/DD/YYYY)
    placeholder?: string; // Placeholder text
    showToday?: boolean; // Whether to show "Today" button
    style?: React.CSSProperties; // Inline styles for the DatePicker
    classNames?: string;
    enabledDates?: { start?: number; end?: number } | null; // Range of enabled dates in epoch
    disabled?: boolean;
    allowedDays?: Number[];
    defaultOpenDate?: number | null; // Optional epoch to open the calendar on
    'data-testid'?: string;
};

const EpochDatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    format = 'MM/DD/YYYY',
    placeholder = 'Select date',
    showToday = true,
    style = {},
    classNames,
    enabledDates,
    disabled,
    allowedDays,
    defaultOpenDate,
    'data-testid': testId,
}) => {
    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            const formattedDate = date.format('MM/DD/YYYY');
            const utcOffset = dayjs(formattedDate).utcOffset();
            const dateUnix = dayjs(formattedDate).add(utcOffset, 'm').utc().startOf('day').unix(); //convert user selected date to UTC 12am unix
            onChange(dateUnix);
        } else {
            onChange(null);
        }
    };

    const disabledDate = (currentDate: Dayjs) => {
        if (allowedDays && !allowedDays.includes(currentDate.day())) {
            return true;
        }
        const currentDateFormatted = currentDate.startOf('day').format('MM/DD/YYYY');
        const updatedCurrentDate = dayjs(currentDateFormatted, 'MM/DD/YYYY');
        if (enabledDates?.start && !enabledDates?.end) {
            const startDateFormatted = dayjs.unix(enabledDates.start).format('MM/DD/YYYY');
            const startDate = dayjs(startDateFormatted, 'MM/DD/YYYY');
            return updatedCurrentDate.isBefore(startDate, 'day');
        }

        if (!enabledDates?.start && enabledDates?.end) {
            const endDateFormatted = dayjs.unix(enabledDates.end).utc().format('MM/DD/YYYY');
            const endDate = dayjs(endDateFormatted, 'MM/DD/YYYY');
            return updatedCurrentDate.isAfter(endDate, 'day');
        }

        if (enabledDates?.start && enabledDates?.end) {
            const startDateFormatted = dayjs.unix(enabledDates.start).format('MM/DD/YYYY');
            const startDate = dayjs(startDateFormatted, 'MM/DD/YYYY');
            const endDateFormatted = dayjs.unix(enabledDates.end).utc().format('MM/DD/YYYY');
            const endDate = dayjs(endDateFormatted, 'MM/DD/YYYY');
            return updatedCurrentDate.isBefore(startDate, 'day') || updatedCurrentDate.isAfter(endDate, 'day');
        }

        return false;
    };

    return (
        <DatePicker
            key={defaultOpenDate ?? 'no-anchor'}
            data-testid={testId}
            value={value ? dayjs.unix(value).utc() : null}
            onChange={handleDateChange}
            format={format} // Date display format
            placeholder={placeholder}
            showToday={showToday}
            style={{ width: '100%', ...style }} // Merge default and custom styles
            className={classNames}
            disabledDate={disabledDate} // Restrict dates
            disabled={disabled}
            defaultPickerValue={
                value
                    ? undefined
                    : defaultOpenDate
                    ? dayjs.unix(defaultOpenDate).local().startOf('day')
                    : dayjs().local().startOf('day')
            }
        />
    );
};

export default EpochDatePicker;
